import { Gender } from '@pflegenavi/shared/api';
import { useCallback, useState, type JSX } from 'react';
import { useEnqueueSnackbarOnce } from '@pflegenavi/web-components';
import { ServiceProviderReceiptBatchRowContainer } from './form/ServiceProviderReceiptBatchRowContainer';
import type { ServiceProviderReceiptBatchRenderRowProps } from './form/ServiceProviderReceiptBatchReceiptsView';
import { useReceiptBatchStore } from './ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';

interface UseServiceProviderRenderReceiptBatchRowResult {
  isLoading: boolean;
  renderRow: (props: ServiceProviderReceiptBatchRenderRowProps) => JSX.Element;
  residents?: Array<{
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    gender: Gender;
  }>;
}

export const useServiceProviderRenderReceiptBatchRow =
  (): UseServiceProviderRenderReceiptBatchRowResult => {
    const [
      residents,
      uploadReceiptImage,
      lineItemConfigurations,
      autoGeneratedReceipts,
      isLoading,
    ] = useReceiptBatchStore(
      useShallow((state) => [
        state.residents,
        state.uploadReceiptImage,
        state.lineItemConfigurations,
        state.formik?.values.generateReceipts,
        state.isLoading,
      ])
    );

    const enqueueSnackbarOnce = useEnqueueSnackbarOnce();

    const [showTabTooltip, setShowTabTooltip] = useState(true);

    const renderRow = useCallback(
      (props: ServiceProviderReceiptBatchRenderRowProps) => {
        const receiptHasResident =
          !!props.receipt.residentId && !!props.receipt.residentName;
        const residentMissing =
          residents.find(
            (resident) => resident.id === props.receipt.residentId
          ) === undefined;
        const allResidents =
          receiptHasResident && residentMissing
            ? [
                ...residents,
                {
                  id: props.receipt.residentId!,
                  name: props.receipt.residentName!,
                  gender: props.receipt.residentGender ?? Gender.Other,
                },
              ]
            : residents;

        return (
          <ServiceProviderReceiptBatchRowContainer
            key={props.receipt.id}
            upload={uploadReceiptImage}
            residents={allResidents}
            lineItemConfigurations={lineItemConfigurations}
            autoGeneratedReceipts={autoGeneratedReceipts ?? false}
            archivedResidents={props.archivedResidents}
            isLoadingResidents={isLoading}
            enqueueSnackbarOnce={enqueueSnackbarOnce}
            showTabTooltip={showTabTooltip}
            setShowTabTooltip={setShowTabTooltip}
            {...props}
          />
        );
      },
      [
        residents,
        uploadReceiptImage,
        lineItemConfigurations,
        autoGeneratedReceipts,
        isLoading,
        enqueueSnackbarOnce,
        showTabTooltip,
      ]
    );

    return {
      isLoading,
      renderRow,
      residents,
    };
  };
